import { createServerFn } from '@tanstack/react-start';
import _ from 'lodash';
import { z } from 'zod';

import { notFound } from '@tanstack/react-router';
import { customer_stories, customer_testimonials, customers } from '#/content';

const fieldIdsSchema = z.array(
  z.object({
    id: z.string(),
    order: z.number().optional(),
  })
);

const listCustomerTestimonialsSchema = z
  .object({
    fields: z
      .object({
        ids: fieldIdsSchema,
      })
      .optional(),
    limit: z.number().optional(),
  })
  .optional();

/**
 * List customer testimonials.
 *
 * @param data.fields (optional) fields to filter by.
 */
export const listCustomerTestimonials = createServerFn({
  method: 'POST',
})
  .validator((payload: unknown) => listCustomerTestimonialsSchema.parse(payload))
  .handler(async (ctx) => {
    return _.chain(customer_testimonials)
      .filter((testimonial) => {
        if (ctx.data?.fields?.ids) {
          return ctx.data.fields.ids.some((field) => field.id === testimonial.id);
        }

        return true;
      })
      .map((testimonial) => {
        const customer = getCustomerById(testimonial.companyId);
        const meta = ctx.data?.fields?.ids?.find((field) => field.id === testimonial.id);

        if (!customer || !meta) {
          return null;
        }

        return {
          ...testimonial,
          ...meta,
          customer,
        };
      })
      .compact()
      .sortBy('order', 'asc')
      .take(ctx.data?.limit ?? 16)
      .value();
  });

const listCustomerLogosSchema = z
  .object({
    fields: z
      .object({
        ids: fieldIdsSchema,
      })
      .optional(),
    limit: z.number().optional(),
  })
  .optional();

export const listCustomerLogos = createServerFn({
  method: 'POST',
})
  .validator((payload: unknown) => listCustomerLogosSchema.parse(payload))
  .handler(async (ctx) => {
    return _.chain(customers)
      .filter((testimonial) => {
        if (ctx.data?.fields?.ids) {
          return ctx.data.fields.ids.some((field) => field.id === testimonial.id);
        }

        return true;
      })
      .map((customer) => {
        const meta = ctx.data?.fields?.ids?.find((field) => field.id === customer.id);

        if (!meta) {
          return null;
        }

        return {
          ...customer,
          ...meta,
        };
      })
      .sortBy('order', 'asc')
      .take(ctx.data?.limit ?? 16)
      .compact()
      .value();
  });

const getCustomerStorySchema = z.object({
  id: z.string(),
});

export const getCustomerStory = createServerFn()
  .validator((payload: unknown) => getCustomerStorySchema.parse(payload))
  .handler(async (ctx) => {
    const story = customer_stories.find((testimonial) => testimonial.id === ctx.data.id);

    if (!story) {
      throw notFound({
        data: { id: ctx.data.id },
      });
    }

    const company = getCustomerById(story.companyId);

    if (!company) {
      throw notFound({
        data: { id: ctx.data.id, companyId: story.companyId },
      });
    }

    return {
      ...story,
      company,
    };
  });

export const listCustomerStories = createServerFn().handler(async () => {
  return _.chain(customer_stories)
    .map((story) => {
      const company = getCustomerById(story.companyId);

      if (!company) {
        throw notFound({
          data: { id: story.id, companyId: story.companyId },
        });
      }

      return {
        ...story,
        company,
      };
    })
    .compact()
    .value();
});

/*
|------------------
| Types
|------------------
*/

export type CustomerStory = Awaited<ReturnType<typeof getCustomerStory>>;

export type CustomerTestimonial = Awaited<ReturnType<typeof listCustomerTestimonials>>[number];

export type CustomerLogo = Awaited<ReturnType<typeof listCustomerLogos>>[number];

/*
|------------------
| Utils
|------------------
*/

function getCustomerById(id: string) {
  return customers.find((customer) => customer.id === id);
}
